import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Premium agency loader — counter, progress bar, logo reveal, curtain exit
const Loader = ({ onDone }) => {
  const [pct, setPct] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const start = performance.now();
    const total = 2400; // 2.4s
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / total, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setPct(Math.floor(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else {
        setTimeout(() => {
          setDone(true);
          setTimeout(() => onDone && onDone(), 900);
        }, 250);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  const word = "MERAKI";

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          className="fixed inset-0 z-[200] overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 1 }}
        >
          {/* Curtain layers that slide up on exit */}
          <motion.div
            className="absolute inset-0"
            style={{ background: "#0a0908" }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.9, ease: [0.83, 0, 0.17, 1] }}
          />
          <motion.div
            className="absolute inset-0 pointer-events-none"
            exit={{ y: "-100%" }}
            transition={{ duration: 0.9, ease: [0.83, 0, 0.17, 1], delay: 0.08 }}
            style={{
              background:
                "radial-gradient(900px 500px at 50% 50%, rgba(201,169,97,0.12), transparent 60%)"
            }}
          />

          {/* Grain on loader */}
          <div className="grain" style={{ zIndex: 1, opacity: 0.08 }} />

          {/* Content */}
          <motion.div
            className="relative z-10 h-full flex flex-col items-center justify-center px-8"
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Top row: brand mark + tagline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="absolute top-8 left-8 right-8 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <img 
                  src="/meraki_solutions_logo.png" 
                  alt="Meraki Logo" 
                  className="h-10 md:h-12 w-auto object-contain brightness-110"
                />
              </div>
              <span className="font-impact text-[11px] tracking-[0.35em] text-[var(--muted)]">
                EST. 2016
              </span>
            </motion.div>

            {/* Big letter reveal */}
            <div className="relative">
              <div className="flex overflow-hidden">
                {word.split("").map((ch, i) => (
                  <motion.span
                    key={i}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.2 + i * 0.06,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                    className="font-serif-accent text-[clamp(80px,15vw,220px)] leading-[0.9] text-[var(--cream)] uppercase font-medium"
                    style={{ display: "inline-block" }}
                  >
                    {ch}
                  </motion.span>
                ))}
              </div>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformOrigin: "left" }}
                className="mt-4 h-px bg-[var(--gold)] w-full origin-left"
              />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-6 text-center font-impact text-[13px] md:text-[15px] tracking-[0.55em] text-[var(--cream-muted)]"
              >
                STORIES · CRAFTED · BRANDS · ENGINEERED
              </motion.div>
            </div>

            {/* Bottom: progress */}
            <div className="absolute bottom-10 left-8 right-8">
              <div className="flex items-end justify-between mb-3">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="font-impact text-[11px] tracking-[0.35em] text-[var(--muted)]"
                >
                  LOADING {pct < 25 ? "ASSETS" : pct < 55 ? "CINEMATICS" : pct < 85 ? "INTERACTIONS" : "FINAL POLISH"}
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="font-impact text-[clamp(40px,6vw,76px)] leading-none text-[var(--gold-bright)]"
                >
                  {String(pct).padStart(3, "0")}
                  <span className="text-[var(--muted)] text-[0.4em] align-top ml-1">%</span>
                </motion.div>
              </div>
              <div className="relative h-px w-full bg-[rgba(201,169,97,0.18)] overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0"
                  style={{
                    width: `${pct}%`,
                    background:
                      "linear-gradient(90deg, rgba(228,199,122,0.3), #e4c77a 60%, #b87333)"
                  }}
                />
                <motion.div
                  className="absolute top-[-3px] w-[6px] h-[6px] rounded-full"
                  style={{
                    left: `calc(${pct}% - 3px)`,
                    background: "#e4c77a",
                    boxShadow: "0 0 12px #e4c77a"
                  }}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Loader;
